import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';

/**
 * LoggerService - Centralized logging service
 *
 * Provides structured logging with different levels
 * Respects ESLint rules (only warn/error in production)
 * Can be extended to send logs to monitoring services (Sentry, LogRocket, etc.)
 */
@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private readonly isDevelopment = !environment.production;

  /**
   * Log informational messages (dev only)
   */
  info(message: string, ...args: unknown[]): void {
    if (this.isDevelopment) {
      // Using console.warn to comply with ESLint (only warn/error allowed)
      console.warn(`ℹ️ [INFO] ${message}`, ...args);
    }
  }

  /**
   * Log success messages (dev only)
   */
  success(message: string, ...args: unknown[]): void {
    if (this.isDevelopment) {
      console.warn(`✅ [SUCCESS] ${message}`, ...args);
    }
  }

  /**
   * Log warning messages
   */
  warn(message: string, ...args: unknown[]): void {
    console.warn(`⚠️ [WARN] ${message}`, ...args);
  }

  /**
   * Log error messages
   */
  error(message: string, error?: unknown, ...args: unknown[]): void {
    console.error(`❌ [ERROR] ${message}`, error, ...args);
  }

  /**
   * Log loading/progress messages (dev only)
   */
  loading(message: string, ...args: unknown[]): void {
    if (this.isDevelopment) {
      console.warn(`🔄 [LOADING] ${message}`, ...args);
    }
  }
}
